import { useToast } from '@/components/ui/toast'
import { escapeRegex } from '@/utils/stringUtils'
import { usePdfOperations } from './usePdfOperations'
import type { PageItem } from './useDocumentState'
import { useDocumentState } from './useDocumentState'

const PART_TOKEN = '(?:Teil|Part)'
const PART_SUFFIX = new RegExp(` ${PART_TOKEN} \\d+$`)

/**
 * Compute the renamed source and new sibling name for a split document.
 *
 * The first split of "Foo" produces "Foo Part 1" / "Foo Part 2". Subsequent
 * splits within the family continue from `max(existing parts) + 1`, surviving
 * gaps and prefix collisions with unrelated documents.
 *
 * Pure: no Vue refs, no i18n. The caller passes `formatPart(base, n)` to render
 * the locale-specific suffix.
 */
export const computeSplitNames = (
  sourceName: string,
  allNames: readonly string[],
  formatPart: (base: string, part: number) => string
): { renamedSource: string | null; newName: string } => {
  const baseName = sourceName.replace(PART_SUFFIX, '')
  const siblingPattern = new RegExp(`^${escapeRegex(baseName)} ${PART_TOKEN} (\\d+)$`)
  let siblingPartOneExists = false
  const familyParts = allNames
    .map((name) => {
      if (name === baseName) return 1
      const match = name.match(siblingPattern)
      if (match) {
        const part = +match[1]
        if (part === 1 && name !== sourceName) siblingPartOneExists = true
        return part
      }
      return null
    })
    .filter((n): n is number => n !== null)
  const nextPart = familyParts.length === 0 ? 2 : Math.max(...familyParts) + 1

  if (sourceName === baseName) {
    if (siblingPartOneExists) {
      return {
        renamedSource: formatPart(baseName, nextPart),
        newName: formatPart(baseName, nextPart + 1)
      }
    }
    return {
      renamedSource: formatPart(baseName, 1),
      newName: formatPart(baseName, nextPart)
    }
  }

  return {
    renamedSource: null,
    newName: formatPart(baseName, nextPart)
  }
}

// Types for the document state interface
type DocumentStateInterface = ReturnType<typeof useDocumentState>

// Types for rotation direction
type RotationDirection = 'left' | 'right'

// Progress callback type
type ProgressCallback = (progress: number) => void

// Clean page type for operations (avoiding circular references)
interface CleanPageItem {
  uuid: string
  filename: string
  pageNumber: number
  actualPageNumber: number
  documentUuid: string
  url: string
  images: any[]
  thumbnails: any[]
}

export const useDocumentOperations = (
  t: (key: string, params?: Record<string, any>) => string,
  documentState: DocumentStateInterface
) => {
  const { toast } = useToast()
  const {
    organizedDocuments,
    markAsChanged,
    removeDocument,
    insertDocument,
    createDocumentGroup,
    generateDocumentName
  } = documentState

  const { createPdfFromPages } = usePdfOperations(t)

  /**
   * Split a document at a specific page index
   */
  const splitDocument = (docIndex: number, pageIndex: number): void => {
    const document = organizedDocuments.value[docIndex]

    if (!document) {
      toast({
        title: t('error'),
        description: t('documentNotFound'),
        variant: 'destructive'
      })
      return
    }

    if (document.pages.length < 2) {
      toast({
        title: t('cannotSplit'),
        description: t('documentMustHavePages'),
        variant: 'destructive'
      })
      return
    }

    if (pageIndex < 0 || pageIndex >= document.pages.length) {
      toast({
        title: t('cannotSplit'),
        description: t('invalidPageIndex'),
        variant: 'destructive'
      })
      return
    }

    if (pageIndex === document.pages.length - 1) {
      toast({
        title: t('cannotSplit'),
        description: t('cannotSplitLastPage'),
        variant: 'destructive'
      })
      return
    }

    const currentPages = document.pages.slice(0, pageIndex + 1)
    const newPages = document.pages.slice(pageIndex + 1)

    document.pages = currentPages

    const { renamedSource, newName } = computeSplitNames(
      document.name,
      organizedDocuments.value.map((d) => d.name),
      (name, part) => t('splitDocumentName', { name, part })
    )
    if (renamedSource !== null) {
      document.name = renamedSource
    }
    const newDocument = createDocumentGroup(newName, newPages)

    insertDocument(docIndex + 1, newDocument)

    markAsChanged()

    toast({
      title: t('documentSplit'),
      description: t('documentSplitGroups', { name1: document.name, name2: newDocument.name }),
      variant: 'default'
    })
  }

  /**
   * Merge two documents by their indices
   */
  const mergeDocumentsByIndex = (sourceDocIndex: number, targetDocIndex: number): void => {
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    const targetDoc = organizedDocuments.value[targetDocIndex]

    if (!sourceDoc || !targetDoc) {
      toast({
        title: t('error'),
        description: t('documentNotFound'),
        variant: 'destructive'
      })
      return
    }

    if (sourceDoc.name === targetDoc.name) {
      toast({
        title: t('error'),
        description: t('cannotMergeDocumentWithItself'),
        variant: 'destructive'
      })
      return
    }

    // Move all pages from source document to target document
    targetDoc.pages.push(...sourceDoc.pages)

    // Remove the source document
    removeDocument(sourceDocIndex)

    markAsChanged()

    toast({
      title: t('success'),
      description: t('documentsMergedInto', {
        sourceName: sourceDoc.name,
        targetName: targetDoc.name
      }),
      variant: 'default'
    })
  }

  /**
   * Merge all documents into one
   */
  const mergeAllDocuments = (): void => {
    if (organizedDocuments.value.length <= 1) {
      toast({
        title: t('error'),
        description: t('needMultipleDocuments'),
        variant: 'destructive'
      })
      return
    }

    const firstDoc = organizedDocuments.value[0]
    const otherDocs = organizedDocuments.value.slice(1)

    // Collect all pages from other documents
    const allPages = [...firstDoc.pages]
    otherDocs.forEach((doc) => {
      allPages.push(...doc.pages)
    })

    // Update first document with all pages
    firstDoc.pages = allPages
    firstDoc.name = t('mergedDocument')

    // Remove all other documents
    organizedDocuments.value.splice(1)

    markAsChanged()

    toast({
      title: t('success'),
      description: t('documentsMerged'),
      variant: 'default'
    })
  }

  /**
   * Duplicate a document
   */
  const duplicateDocument = (docIndex: number): void => {
    const doc = organizedDocuments.value[docIndex]
    if (!doc) return

    const newName = generateDocumentName(t('documentCopyName', { name: doc.name }))
    const newDoc = createDocumentGroup(newName, [...doc.pages])

    insertDocument(docIndex + 1, newDoc)
    markAsChanged()

    toast({
      title: t('success'),
      description: t('documentDuplicated', { name: doc.name }),
      variant: 'default'
    })
  }

  /**
   * Delete a document
   */
  const deleteDocument = (docIndex: number): void => {
    const doc = organizedDocuments.value[docIndex]
    if (!doc) return

    if (confirm(t('confirmDelete'))) {
      removeDocument(docIndex)
      markAsChanged()

      toast({
        title: t('success'),
        description: t('documentDeleted', { name: doc.name }),
        variant: 'default'
      })
    }
  }

  /**
   * Create a new document from selected pages
   */
  const createNewDocumentFromPages = (sourceDocIndex: number, pages: PageItem[]): void => {
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    if (!sourceDoc) return

    const newName = generateDocumentName(t('newDocument'))
    const newDoc = createDocumentGroup(newName, [...pages])

    // Remove pages from source document
    sourceDoc.pages = sourceDoc.pages.filter((page) => !pages.some((p) => p.uuid === page.uuid))

    insertDocument(sourceDocIndex + 1, newDoc)
    markAsChanged()

    toast({
      title: t('success'),
      description: t('documentCreated', { name: newName, count: pages.length }),
      variant: 'default'
    })
  }

  /**
   * Move pages from one document to another
   */
  const movePagesBetweenDocuments = (
    sourceDocIndex: number,
    pages: PageItem[],
    targetDocName: string
  ): void => {
    const sourceDoc = organizedDocuments.value[sourceDocIndex]
    const targetDoc = organizedDocuments.value.find((doc) => doc.name === targetDocName)

    if (!sourceDoc || !targetDoc) {
      toast({
        title: t('error'),
        description: t('documentNotFound'),
        variant: 'destructive'
      })
      return
    }

    // Remove pages from source document
    sourceDoc.pages = sourceDoc.pages.filter((page) => !pages.some((p) => p.uuid === page.uuid))

    // Add pages to target document
    targetDoc.pages.push(...pages)

    // If source document is now empty, remove it
    if (sourceDoc.pages.length === 0) {
      removeDocument(sourceDocIndex)
    }

    markAsChanged()

    toast({
      title: t('success'),
      description: t('pagesMoved', { count: pages.length, targetName: targetDocName }),
      variant: 'default'
    })
  }

  /**
   * Delete specific pages from a document
   */
  const deletePagesFromDocument = (docIndex: number, pages: PageItem[]): void => {
    const currentDoc = organizedDocuments.value[docIndex]
    if (!currentDoc) return

    // Remove pages from document
    currentDoc.pages = currentDoc.pages.filter((page) => !pages.some((p) => p.uuid === page.uuid))

    // If all pages are deleted, remove the entire document
    if (currentDoc.pages.length === 0) {
      removeDocument(docIndex)
    }

    markAsChanged()

    const pageNames = pages
      .map((p) => p.filename || t('pageWithNumber', { number: p.pageNumber || t('unknown') }))
      .join(', ')
    toast({
      title: t('success'),
      description: t('pagesDeleted', { count: pages.length, pageNames }),
      variant: 'default'
    })
  }

  /**
   * Delete a specific page by UUID
   */
  const deletePageByUuid = (docIndex: number, pageUuid: string): void => {
    const currentDoc = organizedDocuments.value[docIndex]
    if (!currentDoc) return

    const pageToDelete = currentDoc.pages.find((page) => page.uuid === pageUuid)
    if (!pageToDelete) {
      toast({
        title: t('error'),
        description: t('pageNotFound'),
        variant: 'destructive'
      })
      return
    }

    // Remove the page
    currentDoc.pages = currentDoc.pages.filter((page) => page.uuid !== pageUuid)

    // If all pages are deleted, remove the entire document
    if (currentDoc.pages.length === 0) {
      removeDocument(docIndex)
    }

    markAsChanged()

    toast({
      title: t('success'),
      description: t('pageDeleted', { pageName: pageToDelete.filename }),
      variant: 'default'
    })
  }

  /**
   * Rotate pages by UUIDs
   */
  const rotatePages = (
    pageUuids: string[],
    direction: RotationDirection,
    pageRotations: Map<string, number>
  ): void => {
    const rotationAmount = direction === 'left' ? -90 : 90

    pageUuids.forEach((pageUuid) => {
      const currentRotation = pageRotations.get(pageUuid) || 0
      const newRotation = (currentRotation + rotationAmount + 360) % 360
      pageRotations.set(pageUuid, newRotation)
    })

    markAsChanged()

    toast({
      title: t('success'),
      description: t('pagesRotated', {
        count: pageUuids.length,
        direction: direction === 'left' ? 'left' : 'right'
      }),
      variant: 'default'
    })
  }

  /**
   * Generate preview for a document group
   */
  const generateDocumentPreview = async (
    docIndex: number,
    pageRotations?: Map<string, number> | Record<string, number>,
    onProgress?: ProgressCallback
  ): Promise<Uint8Array> => {
    const document = organizedDocuments.value[docIndex]

    if (!document || document.pages.length === 0) {
      throw new Error(t('documentNotFoundOrNoPages'))
    }

    try {
      const pdfBytes = await createPdfFromPages(document.pages, pageRotations, onProgress)
      return pdfBytes
    } catch (error) {
      console.error('Error generating preview:', error)
      throw error
    }
  }

  /**
   * Page operations handlers for UI interactions
   */
  const handleCreateNewDocument = (docIndex: number, pages: PageItem[]): void => {
    const sourceDoc = documentState.organizedDocuments.value[docIndex]
    const newName = documentState.generateDocumentName(t('newDocument'))

    const cleanPages: CleanPageItem[] = pages.map((page) => ({
      uuid: page.uuid,
      filename: page.filename,
      pageNumber: page.pageNumber,
      actualPageNumber: page.actualPageNumber,
      documentUuid: page.documentUuid,
      url: page.url,
      images: page.images || [],
      thumbnails: page.thumbnails || []
    }))

    const newDoc = documentState.createDocumentGroup(newName, cleanPages)

    sourceDoc.pages = sourceDoc.pages.filter((page) => !pages.some((p) => p.uuid === page.uuid))
    documentState.organizedDocuments.value.splice(docIndex + 1, 0, newDoc)

    documentState.markAsChanged()
    toast({
      title: t('success'),
      description: t('documentCreated', { name: newName, count: pages.length }),
      variant: 'default'
    })
  }

  const handleMovePages = (docIndex: number, pages: PageItem[], targetDocName: string): void => {
    const sourceDoc = documentState.organizedDocuments.value[docIndex]
    const targetDoc = documentState.findDocumentByName(targetDocName)

    if (!targetDoc) return

    // Create clean page objects to avoid circular references
    const cleanPages: CleanPageItem[] = pages.map((page) => ({
      uuid: page.uuid,
      filename: page.filename,
      pageNumber: page.pageNumber,
      actualPageNumber: page.actualPageNumber,
      documentUuid: page.documentUuid,
      url: page.url,
      images: page.images || [],
      thumbnails: page.thumbnails || []
    }))

    sourceDoc.pages = sourceDoc.pages.filter((page) => !pages.some((p) => p.uuid === page.uuid))
    targetDoc.pages.push(...cleanPages)

    // If source document is now empty, remove it
    if (sourceDoc.pages.length === 0) {
      documentState.removeDocument(docIndex)
    }

    documentState.markAsChanged()
    toast({
      title: t('success'),
      description: t('pagesMoved', { count: pages.length, targetName: targetDocName }),
      variant: 'default'
    })
  }

  const handleDeletePages = (docIndex: number, pages: PageItem[]): void => {
    const sourceDoc = documentState.organizedDocuments.value[docIndex]
    sourceDoc.pages = sourceDoc.pages.filter((page) => !pages.some((p) => p.uuid === page.uuid))

    // If source document is now empty, remove it
    if (sourceDoc.pages.length === 0) {
      documentState.removeDocument(docIndex)
    }

    documentState.markAsChanged()
    toast({
      title: t('success'),
      description: t('pagesDeleted', { count: pages.length }),
      variant: 'default'
    })
  }

  return {
    // Document operations
    splitDocument,
    mergeDocumentsByIndex,
    mergeAllDocuments,
    duplicateDocument,
    deleteDocument,

    // Page operations
    createNewDocumentFromPages,
    movePagesBetweenDocuments,
    deletePagesFromDocument,
    deletePageByUuid,
    rotatePages,

    // Preview operations
    generateDocumentPreview,

    // UI handler operations
    handleCreateNewDocument,
    handleMovePages,
    handleDeletePages
  } as const
}

// Export the return type for external usage
export type DocumentOperations = ReturnType<typeof useDocumentOperations>
